import React, { useEffect, useState } from 'react'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import PropTypes from 'prop-types'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { VFText, Icon, VFButton, Shimmer, ErrorScreen } from '@vfgroup-oneplatform/foundation/Components'
import { useTheme, Colors } from '@vfgroup-oneplatform/foundation/Components/Themes'
import ContentManager from '@vfgroup-oneplatform/foundation/ContentManager'

import { getThemeImages } from '../../Themes'

import StoreCard from '../../Components/StoreCard'
import AddLocation from '../../Components/AddLocation'

import { loadingConfig, shimmerPlaceholderConfig } from './MeetingStep.LoadingConfig'

import styles from './MeetingStep.styles'


const MeetingStep = ({
  addressInfo,
  setAddressInfo,
  defaultAppointmentInfo,
  handleSelectedMeeting,
  citiesOptions,
  isLoading,
  error,
  onTryAgainPress,
  getStoresOpenHours
}) => {

  const theme = useTheme()

  const themedImages = getThemeImages(theme.name)

  const insets = useSafeAreaInsets()

  const [modalVisible, setModalVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [saveInfo, setSaveInfo] = useState(Boolean(addressInfo?.city && addressInfo?.street && addressInfo?.extraData))

  const [selected, setSelected] = useState()
  const [calendar, setCalendar] = useState()
  const [calendarLoading, setCalendarLoading] = useState()

  const toggleModal = () => setModalVisible(prev => !prev)

  const onContinuePress = () => {
    handleSelectedMeeting && handleSelectedMeeting({ ...selected, email: email })
  }

  const continueToNextStep = () => {
    setSelected({
      name: ContentManager.translate('book_appointment_your_address'),
      address: {
        name: addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city
      },
      id: 'custom',
      email: email
    })
    handleSelectedMeeting && handleSelectedMeeting({
      name: ContentManager.translate('book_appointment_your_address'),
      address: {
        name: addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city
      },
      id: 'custom',
      email: email
    })
  }

  useEffect(() => {
    if (JSON.stringify(selected) === JSON.stringify(defaultAppointmentInfo)) {
      setSelected(defaultAppointmentInfo)
    } else if (selected?.address?.name === (addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city)) {
      setSelected({
        name: ContentManager.translate('book_appointment_your_address'),
        address: {
          name: addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city
        },
        id: 'custom'
      })
    } else {
      setSelected(null)
    }
  }, [])

  useEffect(() => {
    setCalendarLoading(true)
    getStoresOpenHours('vfoffice', {
      onSuccess: res => {
        setEmail(res?.email)
        setCalendarLoading(false)
        setCalendar(res?.calendar)
      }
    })
  }, [])


  const renderCard = () => {
    return (
      <StoreCard
        store={defaultAppointmentInfo}
        withViewLocationButton
        showTimeSlotButton={false}
        onPress={() => setSelected(defaultAppointmentInfo)}
        cardContainerStyle={{
          borderColor: Colors.ocean,
          borderWidth: JSON.stringify(selected) === JSON.stringify(defaultAppointmentInfo) ? 2 : 0
        }}
        isLoading={isLoading}
        calendar={calendar}
        calendarLoading={calendarLoading}
      />
    )
  }

  if (error && !isLoading) {
    return (
      <ErrorScreen
        text={error}
        onPress={onTryAgainPress}
      />
    )
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.container}
      scrollEnabled={!isLoading}
      keyboardShouldPersistTaps="always"
    >
      <VFText
        i18nKey={'book_appointment_meeting_step_subtile'}
        style={styles.title(theme)}
      />
      {renderCard()}

      {isLoading ? <Shimmer
        config={loadingConfig(theme)}
        shimmerPlaceholderConfig={shimmerPlaceholderConfig}
      /> : <>
        <VFText
          i18nKey="book_appointment_or"
          style={styles.text(theme)}
        />

        {saveInfo ? <>
          <View style={styles.row}>

            <VFText
              i18nKey="book_appointment_your_address"
              style={styles.addressText}
            />

            <TouchableOpacity
              style={styles.editButton}
              onPress={toggleModal}
            >
              <VFText
                i18nKey="book_appointment_edit_location"
                style={styles.editText(theme)}
              />
              <Icon
                name={themedImages.edit}
                size={20}
              />
            </TouchableOpacity>


          </View>
        </> : <TouchableOpacity
          style={styles.addLocationContainer(theme)}
          onPress={() => setModalVisible(true)}
        >
          <Icon
            name={themedImages.TImages.ic_close.primary}
            size={23}
            style={styles.addIcon}
          />
          <VFText
            i18nKey="book_appointment_add_location"
            style={styles.locationText}
          />
        </TouchableOpacity>}

        {saveInfo && addressInfo?.city && addressInfo?.street && addressInfo?.extraData && <>
          <TouchableOpacity
            style={[styles.addressContainer(theme), {
              borderColor: Colors.ocean,
              borderWidth: selected?.address?.name === (addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city) ? 2 : 0
            }]}
            onPress={() => {
              setSelected({
                name: ContentManager.translate('book_appointment_your_address'),
                address: {
                  name: addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city
                },
                id: 'custom'
              })
            }}
          >
            <Icon
              name={themedImages.icLocation}
              size={30}
            />
            <VFText
              i18nKey={addressInfo?.street + ' ' + addressInfo?.extraData + ', ' + addressInfo?.city}
              style={styles.yourAddress}
            />
          </TouchableOpacity>
        </>}

      </>}
      <View style={styles.buttonContainer(insets)}>
        <VFButton
          title="book_appointment_continue"
          onPress={onContinuePress}
          disabled={!selected || isLoading}
        />
      </View>

      <AddLocation
        isVisible={modalVisible}
        toggleModal={toggleModal}
        addressInfo={addressInfo}
        setAddressInfo={setAddressInfo}
        citiesOptions={citiesOptions}
        setSaveInfo={setSaveInfo}
        continueToNextStep={continueToNextStep}
      />

    </ScrollView >
  )
}
MeetingStep.propTypes = {
  defaultAppointmentInfo: PropTypes.object,
  setAddressInfo: PropTypes.func,
  addressInfo: PropTypes.shape({
    city: PropTypes.string,
    street: PropTypes.string,
    buildingNo: PropTypes.string,
  }),
  handleSelectedMeeting: PropTypes.func,
  citiesOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any,
    label: PropTypes.string
  })),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onTryAgainPress: PropTypes.func,
  getStoresOpenHours: PropTypes.func,
}

export default MeetingStep
